import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CameraRollIcon from '@mui/icons-material/CameraRoll';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef } from '../../../domain';
import {
  DEFAULT_MULTI_ANGLE_STATE,
  buildMultiAngleRequest,
  hasSourceImage,
  type MultiAngleFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  onResult?: (result: OperationResult) => void;
}

export const MultiAnglePanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  onResult,
}) => {
  const [state, setState] = useState<MultiAngleFormState>(DEFAULT_MULTI_ANGLE_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.multiAngle);

  const hasSource = hasSourceImage(source);

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to render multi-angle views.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildMultiAngleRequest(state, sid, source);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled = mutation.isPending || isCreatingSession || !hasSource;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <CameraRollIcon fontSize="small" />
        <Typography variant="subtitle2">Multi-angle</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image of the subject.</Alert>
      )}

      <Stack direction="row" spacing={1}>
        <TextField
          label="Angles"
          type="number"
          value={state.numAngles}
          onChange={e => setState(s => ({ ...s, numAngles: Number(e.target.value) }))}
          size="small"
          inputProps={{ min: 1, max: 8, step: 1 }}
        />
        <TextField
          label="Step (°)"
          type="number"
          value={state.angleStepDegrees}
          onChange={e => setState(s => ({ ...s, angleStepDegrees: Number(e.target.value) }))}
          size="small"
          inputProps={{ min: 5, max: 90, step: 5 }}
        />
      </Stack>

      <FormControlLabel
        control={
          <Switch
            checked={state.preserveIdentity}
            onChange={e => setState(s => ({ ...s, preserveIdentity: e.target.checked }))}
            size="small"
          />
        }
        label="Preserve subject identity"
      />

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Multi-angle failed.'}
        </Alert>
      )}

      {mutation.isPending && (
        <Alert severity="info" icon={<CameraRollIcon fontSize="inherit" />}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Generating {state.numAngles} {state.numAngles === 1 ? 'angle' : 'angles'}…
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Each angle is rendered separately, so this takes longer than a single image.
          </Typography>
          <LinearProgress sx={{ mt: 1 }} />
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <CameraRollIcon />
            )
          }
        >
          {isCreatingSession
            ? 'Starting session…'
            : mutation.isPending
              ? `Rendering ${state.numAngles} ${state.numAngles === 1 ? 'angle' : 'angles'}…`
              : 'Render angles'}
        </Button>
      </Box>
    </Stack>
  );
};
